import { useEffect, useState } from "react";
import {
  VStack,
  HStack,
  FormControl,
  Input,
  Radio,
  Text,
  TextArea,
  Checkbox,
  ScrollView,
} from "native-base";

const AccessibilityForm = ({ onScoreChange, onFormChange, formData }) => {
  const [form, setForm] = useState(formData || {});
  const [score, setScore] = useState(0);
  const [prevScores, setPrevScores] = useState({});

  const updateForm = (field, value, scoreDelta) => {
    setForm((prevForm) => {
      const updatedForm = { ...prevForm, [field]: value };
      onFormChange && onFormChange(updatedForm); // Send to parent
      return updatedForm;
    });

    setPrevScores((prev) => {
      const prevFieldScore = prev[field] || 0;
      const newTotalScore = Math.min(
        5,
        score - prevFieldScore + (scoreDelta || 0)
      );

      // Update score using correct delta
      setScore(newTotalScore);
      onScoreChange(newTotalScore);

      // Store the new field score
      return { ...prev, [field]: scoreDelta };
    });
  };

  useEffect(() => {
    if (formData) {
      setForm({ ...formData });
    }
  }, [formData]);

  const renderCheckboxGroup = (label, name, options) => (
    <FormControl>
      <FormControl.Label>{label}</FormControl.Label>
      <VStack space={2}>
        {options.map((opt) => (
          <Checkbox
            key={opt.value}
            value={opt.value}
            isChecked={form[name]?.includes(opt.value)}
            onChange={(isChecked) => {
              let updatedValues = form[name] || [];
              let updatedScore = form[`${name}Score`] || 0;

              if (isChecked) {
                updatedValues = [...updatedValues, opt.value];
                updatedScore += opt?.score || 0;
              } else {
                updatedValues = updatedValues.filter(
                  (val) => val !== opt.value
                );
                updatedScore -= opt?.score || 0;
              }

              updateForm(name, updatedValues);
              updateForm(`${name}Score`, updatedScore);
            }}
          >
            {opt.label}
          </Checkbox>
        ))}
      </VStack>
    </FormControl>
  );

  const renderRadioGroup = (label, name, options) => (
    <FormControl>
      <FormControl.Label textAlign={'left'}>{label}</FormControl.Label>
      <Radio.Group
        name={name}
        onChange={(value) => {
          const selected = options.find((opt) => opt.value === value);
          if (selected) updateForm(name, value, selected.score);
        }}
        value={form[name] || ""}
      >
        <HStack space={4} flexWrap="wrap">
          {options.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </HStack>
      </Radio.Group>
    </FormControl>
  );

  const renderTextInput = (label, name) => (
    <FormControl>
      <FormControl.Label>{label}</FormControl.Label>
      <Input
        value={form[name] || ""}
        onChangeText={(text) => updateForm(name, text)}
      />
    </FormControl>
  );

  const distanceOptions = [
    ["10m", 0.5],
    ["20m", 0.5],
    ["30m", 0.5],
    ["40m", 0.5],
    ["50m", 0.4],
    ["60m", 0.4],
    ["70m", 0.3],
    ["80m", 0.3],
    ["90m", 0.2],
    ["100m", 0.2],
    ["110m", -0.1],
    ["120m", -0.1],
    ["130m", -0.2],
    ["140m", -0.2],
    ["150m", -0.3],
    ["160m", -0.3],
    ["170m", -0.4],
    ["180m", -0.4],
    ["190m", -0.5],
    ["200m", -0.5],
  ];

  return (
    <ScrollView>
      <VStack space={4} p={4}>
        <Text bold fontSize="lg">
          Accessibility Features
        </Text>
        <Text fontSize={"lg"}>
          Please complete the following questions to inform Piatto of the
          current accessibility of your venue. In joining Piatto, your venue
          will become instantly more accessible, because it will allow customers
          to peruse menus, place orders and pay for those orders, without
          physically entering your venue.
        </Text>
        {renderRadioGroup("Front Entrance Type", "entranceType", [
          { label: "Completely Level", value: "level", score: 0.5 },
          { label: "Slight Lip", value: "lip", score: -1 },
        ])}
        {renderRadioGroup("Is The Door Manual or Automatic?", "doorType", [
          { label: "Manual", value: "manual", score: -1 },
          { label: "Automatic", value: "automatic", score: 0.5 },
        ])}
        {form.doorType === "automatic" &&
          renderRadioGroup(
            "Does This Door Open Automatically or Upon Pressing a Button?",
            "autoDoorMethod",
            [
              { label: "Automatically", value: "auto", score: 0.5 },
              { label: "Button Press", value: "button", score: 0.5 },
            ]
          )}
        {form.doorType === "manual" &&
          renderRadioGroup("Is the door heavy?", "isDoorHeavy", [
            { label: "Yes", value: "yes", score: -1 },
            { label: "No", value: "no", score: -0.5 },
          ])}
        {renderRadioGroup(
          "Are There Likely To Be Obstacles, Including Other Dining Guests, Behind The Door Or Near To The Door's Opening Which Might Impede Its Opening?",
          "doorObstacles",
          [
            { label: "Yes", value: "yes", score: -1 },
            { label: "No", value: "no", score: 0.5 },
          ]
        )}
        {renderRadioGroup(
          "Is The Front, Main, Entrance To Your Business On The Same Level As It's Surroundings Or Are Guests Required To Traverse A Ramp, Steps Or Use An Elevator?",
          "entranceAccess",
          [
            { label: "Same Level", value: "same", score: 1 },
            { label: "Ramp", value: "ramp", score: 0.5 },
            { label: "Steps", value: "steps", score: -3 },
            { label: "Elevator", value: "elevator", score: -0.2 },
          ]
        )}
        {form.entranceAccess === "ramp" &&
          renderRadioGroup(" Is the ramp fixed or removable?", "ramp", [
            { label: "Fixed", value: "fixed", score: 0 },
            { label: "Removable", value: "removable", score: 0 },
          ])}
        {form.ramp === "Removable" &&
          renderRadioGroup(
            "If the ramp is Removable, is someone available and on the look out for someone with accessibility needs, to put the ramp in place?",
            "rampRemoveAble",
            [
              { label: "Yes", value: "yes", score: 0 },
              { label: "No", value: "no", score: 0 },
            ]
          )}
        {form.entranceAccess === "elevator" &&
          renderRadioGroup(
            "Does the elevator require a key to operate?",
            "elevatorNeedsKey",
            [
              { label: "Yes", value: "yes", score: -0.5 },
              { label: "No", value: "no", score: 0.2 },
            ]
          )}
        {form.elevatorNeedsKey === "yes" &&
          renderRadioGroup(
            "Is the person with the key constantly on the lookout for disabled persons requiring use of the elevator?",
            "keyHolderAlert",
            [
              { label: "Yes", value: "yes", score: 0 },
              { label: "No", value: "no", score: -0.5 },
            ]
          )}
        {form.keyHolderAlert === "no" &&
          renderTextInput(
            "How would someone grab the attention of the person with the key upon arriving at your venue shall they require use of the elevator?",
            "keyAttentionMethod"
          )}

        {form.entranceAccess === "elevator" &&
          renderRadioGroup(
            "How far from your venue's main entrance is the opening of the elevator on the same level?",
            "elevatorDistanceFromEntrance",
            distanceOptions.map(([label, score]) => ({
              label,
              value: label,
              score,
            }))
          )}

        {form.entranceAccess === "ramp" &&
          form.rampType === "fixed" &&
          renderRadioGroup(
            "Has the ramp been constructed to standards (1:14 gradient)?",
            "rampGradientCompliance",
            [
              { label: "Yes", value: "yes", score: 0.5 },
              { label: "No", value: "no", score: -1 },
            ]
          )}

        {renderRadioGroup(
          "Are there disabled parking bays nearby to your venue?",
          "hasDisabledParking",
          [
            { label: "Yes", value: "yes", score: 0.5 },
            { label: "No", value: "no", score: -0.5 },
          ]
        )}

        {form.flatSurfaceFromParking === "no" &&
          renderRadioGroup(
            "Is there a slight incline/decline or a greater incline/decline?",
            "surfaceInclineType",
            [
              { label: "Slight Incline", value: "slightIncline", score: -0.2 },
              { label: "Slight Decline", value: "slightDecline", score: -0.2 },
              {
                label: "Greater Incline",
                value: "greaterIncline",
                score: -0.5,
              },
              {
                label: "Greater Decline",
                value: "greaterDecline",
                score: -0.5,
              },
            ]
          )}

        {renderCheckboxGroup(
          "What is the surface material from disabled car park to venue?",
          "surfaceMaterial",
          [
            { label: "Bitumen", value: "bitumen", score: 0.5 },
            { label: "Cement", value: "cement", score: 0.5 },
            { label: "Grass", value: "grass", score: -0.5 },
            { label: "Sand", value: "sand", score: -1 },
          ]
        )}

        {renderRadioGroup(
          "Are the disabled parking bays certified as such (wider than standard)?",
          "certifiedDisabledParking",
          [
            { label: "Yes", value: "yes", score: 0.5 },
            { label: "No", value: "no", score: 0.2 },
          ]
        )}

        {renderRadioGroup(
          "If a footpath is present near the disabled carpark, is it flat or is there a curb-cutout with incline?",
          "footpathFromParking",
          [
            { label: "Flat", value: "flat", score: 0.5 },
            {
              label: "Curb-Cutout with Slight Incline",
              value: "curbCut",
              score: 0.3,
            },
          ]
        )}

        <Text bold fontSize="md" mt={4}>
          Accessible Bathroom
        </Text>
        {renderRadioGroup(
          "Does your venue have an accessible bathroom?",
          "bizDisBathType",
          [
            { label: "None / Unknown", value: "none", score: 0 },
            { label: "Accessible (AS 1428.1)", value: "accessible_as1428", score: 1 },
            { label: "Ambulant", value: "ambulant", score: 0.5 },
            { label: "Changing Places", value: "changing_places", score: 1.5 },
          ]
        )}
        {form.bizDisBathType === "accessible_as1428" && (
          <Text fontSize="xs" color="coolGray.500">
            Full wheelchair-accessible bathroom per Australian Standard 1428.1.
          </Text>
        )}
        {form.bizDisBathType === "ambulant" && (
          <Text fontSize="xs" color="coolGray.500">
            Grab rails and some accessibility features, but not full wheelchair turning space.
          </Text>
        )}
        {form.bizDisBathType === "changing_places" && (
          <Text fontSize="xs" color="coolGray.500">
            Adult change table, ceiling hoist, and carer space.
          </Text>
        )}

        {form.bizDisBathType && form.bizDisBathType !== "none" &&
          renderCheckboxGroup(
            "Which features does the accessible bathroom have?",
            "bizDisBathFeatures",
            [
              { label: "Grab Rails", value: "grab_rails", score: 0 },
              { label: "Transfer Space (Left)", value: "transfer_space_left", score: 0 },
              { label: "Transfer Space (Right)", value: "transfer_space_right", score: 0 },
              { label: "Transfer Space (Both Sides)", value: "transfer_space_both", score: 0 },
              { label: "Emergency Call Button", value: "emergency_call", score: 0 },
              { label: "Step-Free Access to Bathroom", value: "step_free_access", score: 0 },
              { label: "Unisex / Gender-Neutral", value: "unisex", score: 0 },
              { label: "Baby Change Table", value: "baby_change", score: 0 },
              { label: "Adult Change Table", value: "adult_change_table", score: 0 },
              { label: "Ceiling Hoist", value: "ceiling_hoist", score: 0 },
            ]
          )}

        <Text bold fontSize="md" mt={4}>
          Describe Your Venue for Vision-Impaired Guests
        </Text>

        <FormControl>
          <FormControl.Label
            htmlFor="bizDisEntrance"
            _text={{ fontWeight: "medium" }}
          >
            Describe your entrance and how to find it
          </FormControl.Label>
          <Text
            fontSize="xs"
            color="coolGray.500"
            mb={1}
            nativeID="bizDisEntrance-help"
          >
            Help someone who's vision impaired picture arriving. What street is
            the door on? What's it near or between? Is there a step or ramp? Is
            the door manual, automatic, push or pull? Any landmark like a bus
            stop?
          </Text>
          <TextArea
            nativeID="bizDisEntrance"
            value={form.bizDisEntrance || ""}
            onChangeText={(text) => updateForm("bizDisEntrance", text)}
            placeholder="Front door is on Logan Rd, between the pharmacy and the bakery, about 20m from the bus stop. Glass door, opens automatically, no step — street level, with a tactile mat just inside."
            maxLength={600}
            totalLines={4}
            aria-describedby="bizDisEntrance-help"
            accessibilityLabel="Describe your entrance and how to find it"
          />
        </FormControl>

        {form.bizDisBathType && form.bizDisBathType !== "none" && (
          <FormControl>
            <FormControl.Label
              htmlFor="bizDisBathDir"
              _text={{ fontWeight: "medium" }}
            >
              Directions to the accessible bathroom, from the front door
            </FormControl.Label>
            <Text
              fontSize="xs"
              color="coolGray.500"
              mb={1}
              nativeID="bizDisBathDir-help"
            >
              Describe the path step by step — distance, turns, landmarks, and
              the door.
            </Text>
            <TextArea
              nativeID="bizDisBathDir"
              value={form.bizDisBathDir || ""}
              onChangeText={(text) => updateForm("bizDisBathDir", text)}
              placeholder="From the front door, head straight past the counter on your right, about 15 steps to the back. Accessible bathroom is on the left, push door, sign at hand height."
              maxLength={600}
              totalLines={4}
              aria-describedby="bizDisBathDir-help"
              accessibilityLabel="Directions to the accessible bathroom, from the front door"
            />
          </FormControl>
        )}

        <FormControl>
          <FormControl.Label
            htmlFor="bizDisStdBathDir"
            _text={{ fontWeight: "medium" }}
          >
            Directions to the standard bathrooms, from the front door
          </FormControl.Label>
          <Text
            fontSize="xs"
            color="coolGray.500"
            mb={1}
            nativeID="bizDisStdBathDir-help"
          >
            Same idea — guide someone from the entrance.
          </Text>
          <TextArea
            nativeID="bizDisStdBathDir"
            value={form.bizDisStdBathDir || ""}
            onChangeText={(text) => updateForm("bizDisStdBathDir", text)}
            placeholder="Turn right just inside the entrance, past the booths; bathrooms are down a short hallway on the right."
            maxLength={600}
            totalLines={4}
            aria-describedby="bizDisStdBathDir-help"
            accessibilityLabel="Directions to the standard bathrooms, from the front door"
          />
        </FormControl>
      </VStack>
    </ScrollView>
  );
};

export default AccessibilityForm;
